import { useState } from "react";
import { Link } from "react-router-dom";

import {
  NO_CONTACT_EXIT_DONE_REPUBLISHED,
  NO_CONTACT_EXIT_REPUBLISH_LABEL,
} from "../../lib/no-contact-exit-copy";
import { alreadyReviewedMessage } from "../../lib/review-ask";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent } from "../ui/dialog";
import { RadioGroup } from "../ui/radio-group";

/**
 * DisconnectMarketplaceDialog — three-step friction flow for marketplace
 * disconnect (M5 — plan 255, tasks 1127-1131).
 *
 * Only launched for team members whose link is `connection_method === 'marketplace'`.
 * Non-marketplace links keep the existing Your Team disconnect dialog.
 *
 * Steps:
 *   1. Confirm — plain consequences: "Ends your connection with [Agent]. Your
 *      passport can go back on the market."
 *   2. Required reason — six-value enum radio; "other" reveals a required
 *      free-text field; submit blocked until reason (+ note for "other") filled.
 *   3. Review — optional 1-5 star rating + free text, or Skip. Submission
 *      (or its failure) never blocks or delays reaching "done" — the
 *      disconnect itself already fired on advancing from step 2 to step 3.
 *
 * Done state: "You're disconnected" — the passport is OFF the market (a
 * disconnect never auto-republishes it) + a "Re-publish passport" CTA
 * linking to the in-passport publish control.
 *
 * Handlers are injected props (fixtures in M5; M8 wires live endpoints).
 * Submit shape: `POST /api/marketplace/connections/:id/disconnect`
 *   body `{ reason, note? }`
 * The buyer sees ONLY the done state — never payment/refund vocabulary.
 */

import {
  DISCONNECT_STEP1_CONSEQUENCE,
  DISCONNECT_REASON_LABELS,
} from "./DisconnectMarketplaceDialog.helpers";

// ── Exported copy constants (scenario 1136 / 1141 pin these) ─────────────────

/** Done-state headline (scenario 1141). Disconnect never auto-republishes. */
export const DISCONNECT_DONE_HEADLINE = "You're disconnected";

/** Done-state re-publish nudge (scenario 1141). */
export const DISCONNECT_DONE_NUDGE =
  "Your Buyer Passport is off the market. Re-publish it whenever you're ready for agents to reach out again.";

/**
 * Did the server confirm the passport is on the market again? Only a literal
 * `republished: true` counts; a handler that resolves with nothing (every
 * caller but the banner), a failed publish, or a malformed body is "no".
 */
function wasRepublished(result: unknown): boolean {
  return (
    typeof result === "object" &&
    result !== null &&
    (result as { republished?: unknown }).republished === true
  );
}

// ── Six-value reason enum (scenarios 1137/1138/1139) ─────────────────────────

export type DisconnectReason =
  | "chose_someone_else"
  | "not_ready_to_buy"
  | "agent_unresponsive"
  | "not_a_good_fit"
  | "incentive_not_honored"
  | "other";

const REASON_ORDER: DisconnectReason[] = [
  "chose_someone_else",
  "not_ready_to_buy",
  "agent_unresponsive",
  "not_a_good_fit",
  "incentive_not_honored",
  "other",
];

// ── Submit shape ──────────────────────────────────────────────────────────────

export interface DisconnectParams {
  connectionId: string;
  reason: DisconnectReason;
  /** Present only when reason === "other" and non-empty. */
  note?: string;
  /**
   * Plan 462: "put my passport back on the market" in the same step. Present
   * (as `true`) ONLY when the dialog offered the choice (`offerRepublish`)
   * and the buyer checked it; every other flow omits the key entirely.
   */
  republish?: boolean;
}

/**
 * What a disconnect handler may hand back (plan 462). The done state reads
 * `republished` to choose between "back on the market" and the re-publish
 * link; a handler that returns nothing (every pre-existing caller) reads as
 * `republished: false`.
 */
export interface DisconnectResult {
  republished?: boolean;
}

export type DisconnectHandler = (
  params: DisconnectParams,
) => Promise<DisconnectResult | void>;

// ── Step machine ──────────────────────────────────────────────────────────────

export type DialogStep = "confirm" | "reason" | "review" | "done";

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DisconnectMarketplaceDialogProps {
  /** Whether the dialog is open. Managed by the caller (YourTeamListView / RelationshipProfilePage). */
  open: boolean;
  /** Called when the dialog should close (after done, cancel, or ESC). */
  onClose: () => void;
  /** Agent's display name — drives copy like "Ends your connection with [Agent]". */
  agentName: string;
  /**
   * Connection id for the disconnect call.
   * Resolved by the caller via `GET /api/marketplace/connection?role=agent` fixture.
   * M8 wires the live GET.
   */
  connectionId: string;
  /**
   * M5 fixture handler — caller provides a no-op or controlled fixture.
   * M8 wires: `(params) => disconnect(getToken, params.connectionId, { reason: params.reason, note: params.note })`.
   * Called on advancing from step 2 ("reason") to step 3 ("review").
   * The buyer sees only the done state — never payment/refund vocabulary.
   */
  onDisconnect: DisconnectHandler;
  /**
   * The other party's user id, for the optional post-disconnect review
   * (step 3). Optional so callers not yet wired for a pro user id (e.g.
   * YourTeamPage's still-fixture `connectionId`) degrade gracefully — the
   * submit control simply doesn't render without both this and
   * `onSubmitReview`, leaving Skip as the only step-3 action.
   */
  proUserId?: string;
  /**
   * Submits the buyer's optional star rating + note. A rejection is
   * swallowed — review submission NEVER blocks, delays, or rolls back the
   * disconnect, which already completed on the step 2 → step 3 transition.
   * Mirrors `handleSubmitReason`'s swallow-and-continue discipline.
   */
  /**
   * True when the buyer has already reviewed this pro (the server offers no
   * worked_together ask). Reviews are never edited (operator 2026-10-09), so
   * step 3 then shows "You've already reviewed {name}" with no form, and the
   * only action is the disconnect itself. The step used to pre-fill the old
   * review and say "editing replaces it", which the API now refuses.
   */
  alreadyReviewed?: boolean;
  onSubmitReview?: (params: {
    proUserId: string;
    stars: number;
    body: string;
  }) => Promise<void>;
  /**
   * Preview/test seam only — seeds the dialog at a specific step so the DEV
   * `/preview/marketplace/disconnect-dialog?scenario=step2` route can
   * screenshot step 2 without clicking through step 1 first.
   *
   * MUST NOT change real-user behavior: the dialog always opens at
   * `"confirm"` in production (the SettingsPage caller never passes this).
   * Default: `"confirm"`.
   */
  initialStep?: DialogStep;
  /**
   * Plan 462: the reason preselected on step 2 (the no-contact banner opens
   * the dialog with `agent_unresponsive`). The buyer can still change it.
   * Absent keeps the dialog exactly as every other caller has it: no
   * preselection.
   */
  initialReason?: DisconnectReason;
  /**
   * Plan 462: adds the "Put my passport back on the market" choice to step 2,
   * UNCHECKED by default. Only the no-contact banner sets it; the Your Team,
   * Browse, check-in and relationship-page dialogs never show the control and
   * never send `republish`.
   */
  offerRepublish?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function DisconnectMarketplaceDialog({
  open,
  onClose,
  agentName,
  connectionId,
  onDisconnect,
  proUserId,
  alreadyReviewed = false,
  onSubmitReview,
  initialStep = "confirm",
  initialReason,
  offerRepublish = false,
}: DisconnectMarketplaceDialogProps) {
  const [step, setStep] = useState<DialogStep>(initialStep);
  const [selectedReason, setSelectedReason] = useState<DisconnectReason | null>(
    initialReason ?? null,
  );
  const [otherNote, setOtherNote] = useState("");
  const [republish, setRepublish] = useState(false);
  const [republished, setRepublished] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewStars, setReviewStars] = useState(0);
  const [reviewNote, setReviewNote] = useState("");
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  const reasonIsValid =
    selectedReason !== null &&
    (selectedReason !== "other" || otherNote.trim().length > 0);

  const handleClose = () => {
    if (isSubmitting) return;
    // Reset state on close so the dialog starts fresh on re-open.
    // In real use initialStep === "confirm" so this always resets to step 1.
    setStep(initialStep);
    setSelectedReason(initialReason ?? null);
    setOtherNote("");
    setRepublish(false);
    setRepublished(false);
    setIsSubmitting(false);
    setReviewStars(0);
    setReviewNote("");
    setIsSubmittingReview(false);
    onClose();
  };

  const handleAdvanceToReason = () => {
    setStep("reason");
  };

  // Advancing from the reason step does NOT disconnect. Review capture
  // requires an ACTIVE consumer_pro_link, and disconnecting ends exactly that
  // link — so disconnecting first made every post-disconnect review fail
  // eligibility (403 not_eligible), swallowed, while the dialog still showed
  // success. The disconnect is therefore the LAST act, run from both step-3
  // exits below.
  const handleSubmitReason = () => {
    if (!selectedReason || !reasonIsValid) return;
    setStep("review");
  };

  // runDisconnect is the single disconnect call site, reached from BOTH the
  // skip and the submit path so "disconnect executes identically with zero
  // review input" stays literally true.
  const runDisconnect = async () => {
    if (!selectedReason) return;
    setIsSubmitting(true);
    try {
      const result = await onDisconnect({
        connectionId,
        reason: selectedReason,
        note: selectedReason === "other" && otherNote.trim() ? otherNote.trim() : undefined,
        // Omitted entirely unless the control was offered AND checked.
        ...(offerRepublish && republish ? { republish: true } : {}),
      });
      setRepublished(wasRepublished(result));
      setStep("done");
    } catch {
      // Keep the buyer on the review step rather than crashing or claiming a
      // disconnect that did not happen; they can retry from here.
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipReview = () => {
    void runDisconnect();
  };

  const handleSubmitReview = async () => {
    setIsSubmittingReview(true);
    try {
      if (onSubmitReview && proUserId) {
        await onSubmitReview({
          proUserId,
          stars: reviewStars,
          body: reviewNote,
        });
      }
    } catch {
      // Swallow — the review is optional and NEVER-GATING. A failed review
      // must not prevent the disconnect the buyer actually asked for.
    } finally {
      setIsSubmittingReview(false);
    }
    // Runs whether the review succeeded, failed, or was never attempted.
    await runDisconnect();
  };

  const dialogTitle =
    step === "confirm"
      ? "Disconnect from your agent?"
      : step === "reason"
        ? "Why are you disconnecting?"
        : step === "review"
          ? "One more thing"
          : "Disconnected";

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent
        className="max-w-md bg-popover"
        showClose={!isSubmitting}
        title={dialogTitle}
      >
        {/* ── Step 1: Confirm ── */}
        {step === "confirm" && (
          <div className="space-y-5 py-2" data-testid="disconnect-step-1">
            <p className="text-sm leading-6 text-foreground">
              {DISCONNECT_STEP1_CONSEQUENCE(agentName)}
            </p>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleAdvanceToReason}>Continue</Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Required reason ── */}
        {step === "reason" && (
          <div className="space-y-4 py-2" data-testid="disconnect-step-2">
            <p className="text-sm text-muted-foreground">
              Your feedback helps us improve — this isn't shared with your agent.
            </p>
            {/*
             * Uses defaultValue (uncontrolled) rather than a controlled value prop:
             * - The step-2 div unmounts when the user navigates away (Back/Continue),
             *   so on remount defaultValue re-seeds from the current selectedReason.
             * - This avoids the "uncontrolled → controlled" React warning that fires
             *   when value starts as undefined and becomes a string on first selection.
             * - onValueChange keeps the external selectedReason state in sync.
             */}
            <RadioGroup
              className="space-y-2"
              defaultValue={selectedReason ?? undefined}
              onValueChange={(v) => {
                const reason = v as DisconnectReason;
                setSelectedReason(reason);
                if (reason !== "other") setOtherNote("");
              }}
              options={REASON_ORDER.map((reason) => ({
                value: reason,
                label: DISCONNECT_REASON_LABELS[reason],
              }))}
            />

            {/* "other" free-text (scenario 1139) */}
            {selectedReason === "other" && (
              <textarea
                data-testid="disconnect-other-note"
                className="w-full rounded-xl border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="Tell us a bit more (required)"
                rows={3}
                value={otherNote}
                onInput={(e) => setOtherNote((e.target as HTMLTextAreaElement).value)}
                onChange={(e) => setOtherNote(e.target.value)}
                aria-label="Additional context (required)"
                aria-required="true"
              />
            )}

            {offerRepublish && (
              <Checkbox
                checked={republish}
                label={NO_CONTACT_EXIT_REPUBLISH_LABEL}
                onCheckedChange={setRepublish}
                testId="disconnect-republish-checkbox"
              />
            )}

            <Button
              data-testid="disconnect-submit-reason"
              className="w-full"
              disabled={!reasonIsValid || isSubmitting}
              loading={isSubmitting}
              onClick={() => void handleSubmitReason()}
            >
              Disconnect
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setStep("confirm")}
              disabled={isSubmitting}
            >
              Back
            </Button>
          </div>
        )}

        {/* ── Step 3: Optional review — stars + text, or Skip ── */}
        {step === "review" && alreadyReviewed && (
          <div className="space-y-5 py-2" data-testid="disconnect-step-3">
            <p
              className="text-sm text-muted-foreground"
              data-testid="disconnect-review-already"
            >
              {alreadyReviewedMessage(agentName)}
            </p>
            <Button
              data-testid="disconnect-review-skip"
              className="w-full"
              disabled={isSubmitting}
              loading={isSubmitting}
              onClick={handleSkipReview}
            >
              Disconnect
            </Button>
          </div>
        )}

        {step === "review" && !alreadyReviewed && (
          <div className="space-y-5 py-2" data-testid="disconnect-step-3">
            <div className="rounded-xl border border-border/70 p-4">
              <p className="text-sm font-semibold text-foreground">
                Want to leave a review?{" "}
                <span className="font-normal text-muted-foreground">
                  (optional)
                </span>
              </p>
              <div
                className="mt-3 flex gap-1"
                data-testid="disconnect-review-stars"
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-label={`${value} star${value === 1 ? "" : "s"}`}
                    aria-pressed={reviewStars >= value}
                    disabled={isSubmittingReview}
                    onClick={() => setReviewStars(value)}
                    className="text-2xl leading-none text-muted-foreground disabled:opacity-50"
                  >
                    <span aria-hidden="true">
                      {reviewStars >= value ? "★" : "☆"}
                    </span>
                  </button>
                ))}
              </div>
              <textarea
                data-testid="disconnect-review-note"
                className="mt-3 w-full rounded-xl border border-border/60 bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                placeholder="Your review... (optional)"
                rows={3}
                value={reviewNote}
                disabled={isSubmittingReview}
                onInput={(e) =>
                  setReviewNote((e.target as HTMLTextAreaElement).value)
                }
                onChange={(e) => setReviewNote(e.target.value)}
                aria-label="Review text (optional)"
              />
            </div>
            {onSubmitReview && proUserId && (
              <Button
                data-testid="disconnect-review-submit"
                className="w-full"
                disabled={reviewStars === 0 || isSubmittingReview}
                loading={isSubmittingReview}
                onClick={() => void handleSubmitReview()}
              >
                Submit review
              </Button>
            )}
            {onSubmitReview && proUserId && reviewStars === 0 ? (
              // The submit is gated on a rating, and previously said nothing
              // about it: a buyer could type a full review and find the only
              // live control was "Skip".
              <p
                className="text-center text-xs text-muted-foreground"
                data-testid="disconnect-review-stars-hint"
              >
                Pick a star rating to post your review — the text is optional.
              </p>
            ) : null}
            <Button
              data-testid="disconnect-review-skip"
              variant="outline"
              className="w-full"
              disabled={isSubmittingReview}
              onClick={handleSkipReview}
            >
              Skip
            </Button>
          </div>
        )}

        {/* ── Done state (scenario 1141) ── */}
        {step === "done" && (
          <div className="space-y-5 py-2" data-testid="disconnect-done-state">
            <p
              className="text-base font-semibold text-foreground"
              data-testid="disconnect-done-headline"
            >
              {DISCONNECT_DONE_HEADLINE}
            </p>
            {republished ? (
              <p
                className="text-sm text-muted-foreground"
                data-republished="true"
                data-testid="disconnect-done-nudge"
              >
                {NO_CONTACT_EXIT_DONE_REPUBLISHED}
              </p>
            ) : (
              <>
                <p
                  className="text-sm text-muted-foreground"
                  data-republished="false"
                  data-testid="disconnect-done-nudge"
                >
                  {DISCONNECT_DONE_NUDGE}
                </p>
                <Button asChild className="w-full" variant="outline">
                  <Link
                    data-testid="disconnect-republish-cta"
                    to="/dashboard?view=passport#passport-publish-action"
                    onClick={handleClose}
                  >
                    Re-publish passport
                  </Link>
                </Button>
              </>
            )}
            <Button className="w-full" onClick={handleClose}>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
